export function useLocalStorage() {
	/**
	 * Gets the appropriate storage based on options
	 * @param {Object} options - Options object
	 * @returns {Storage} localStorage or sessionStorage
	 */
	function getStorage(options = {}) {
		return options.storage === 'session' ? sessionStorage : localStorage;
	}

	/**
	 * Creates/saves data to localStorage or sessionStorage
	 * @example
	 * create('users', [{ id: 1, name: 'John' }])
	 * create('settings', { theme: 'dark', lang: 'en' })
	 * create('tempData', { temp: true }, { storage: 'session' })
	 */
	function create(key, value, options = {}) {
		if (process.server) return;
		const storage = getStorage(options);
		storage.setItem(key, JSON.stringify(value));
	}

	/**
	 * Gets data from localStorage or sessionStorage with optional filtering
	 * @example
	 * get('users') // Returns all users
	 * get('users', { condition: user => user.active }) // Returns only active users
	 * get('users', { condition: user => user.id === 1, single: true }) // Returns single user object instead of array
	 * get('tempData', { storage: 'session' }) // Gets data from sessionStorage
	 * get('currentUser', { single: true }) // Returns object directly if it's a single item array
	 */
	function get(key, options = {}) {
		if (process.server) return;
		const storage = getStorage(options);
		const item = storage.getItem(key);

		if (!item || item === 'undefined' || item === 'null') return null;

		try {
			const parsedItem = JSON.parse(item);
			if (parsedItem === undefined || parsedItem === null) return null;
			let result = parsedItem;

			if (options?.condition) {
				if (Array.isArray(parsedItem)) {
					result = parsedItem.filter(options.condition);
				} else {
					return null;
				}
			}

			if (options?.single && Array.isArray(result) && result.length === 1) {
				return result[0];
			}

			// Return null if options.single is true but result has 0 or multiple items
			if (options?.single && Array.isArray(result) && result.length !== 1) {
				return null;
			}

			return result;
		} catch (error) {
			console.warn(`Failed to parse storage item "${key}":`, error.message);
			return null;
		}
	}

	/**
	 * Removes data from localStorage or sessionStorage completely or conditionally
	 * @example
	 * remove('users') // Removes entire 'users' key
	 * remove('users', { condition: user => user.id === 1 }) // Removes only user with id 1
	 * remove('tempData', { storage: 'session' }) // Removes from sessionStorage
	 */
	function remove(key, options = {}) {
		if (process.server) return;
		const storage = getStorage(options);
		if (options?.condition) {
			const item = storage.getItem(key);
			if (!item) return;
			const filteredItems = JSON.parse(item).filter(item => !options.condition(item));
			storage.setItem(key, JSON.stringify(filteredItems));
		} else {
			storage.removeItem(key);
		}
	}

	/**
	 * Updates data in localStorage or sessionStorage - replaces entire value or merges with specific items
	 * If condition doesn't find item in array, adds it as new item
	 * @example
	 * update('settings', { theme: 'light' }) // Replaces entire settings object
	 * update('users', { name: 'Jane' }, { condition: user => user.id === 1 }) // Updates only name for user with id 1
	 * update('users', user => ({ lastLogin: new Date() }), { condition: user => user.id === 1 }) // Updates using function
	 * update('tempData', { updated: true }, { storage: 'session' }) // Updates in sessionStorage
	 * update('profile', { email: 'new@email.com' }, { condition: profile => profile.id === 1 }) // Updates object directly
	 * update('users', { id: 99, name: 'New User' }, { condition: user => user.id === 99 }) // Adds new user if not found
	 */
	function update(key, value, options = {}) {
		if (process.server) return;
		const storage = getStorage(options);
		if (options?.condition) {
			const items = storage.getItem(key);

			// If no existing data and we have a condition, create new array with the item
			if (!items) {
				return storage.setItem(key, JSON.stringify([value]));
			}

			const parsedItems = JSON.parse(items);

			// Handle arrays
			if (Array.isArray(parsedItems)) {
				let itemFound = false;
				const updatedItems = parsedItems.map(item => {
					if (options.condition(item)) {
						itemFound = true;
						if (typeof item !== 'object' || item === null) return value;
						return {...item, ...value};
					}
					return item;
				});

				// If no item was found, add as new item
				if (!itemFound) {
					updatedItems.push(value);
				}

				storage.setItem(key, JSON.stringify(updatedItems));
			}
			// Handle objects
			else if (typeof parsedItems === 'object' && parsedItems !== null) {
				if (options.condition(parsedItems)) {
					const updatedItem = {...parsedItems, ...value};
					storage.setItem(key, JSON.stringify(updatedItem));
				}
			}
		} else {
			storage.setItem(key, JSON.stringify(value));
		}
	}

	/**
	 * Inserts/appends new data to existing storage item without replacing it
	 * @example
	 * insert('users', { id: 100, name: 'New User' }) // Appends new user to existing users array
	 * insert('recentItems', { id: 5, title: 'Item' }) // Appends to existing array
	 * insert('tempData', { temp: true }, { storage: 'session' }) // Inserts into sessionStorage
	 * insert('settings', { newSetting: 'value' }) // If existing data is object, converts to array and appends
	 */
	function insert(key, value, options = {}) {
		if (process.server) return;
		const storage = getStorage(options);
		const items = storage.getItem(key);

		if (!items) {
			// If no existing data, create new array with the item
			storage.setItem(key, JSON.stringify([value]));
		} else {
			try {
				const parsedItems = JSON.parse(items);

				if (Array.isArray(parsedItems)) {
					// Append to existing array
					parsedItems.push(value);
					storage.setItem(key, JSON.stringify(parsedItems));
				} else {
					// If existing data is not an array, convert to array and append
					storage.setItem(key, JSON.stringify([parsedItems, value]));
				}
			} catch (error) {
				// If parsing fails, create new array with the value
				storage.setItem(key, JSON.stringify([value]));
			}
		}
	}

	return {
		create,
		get,
		remove,
		update,
		insert,
	};
}
